import React, { createContext, useContext, useState, useCallback, useMemo } from "react";

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  const add = useCallback((item, qty = 1) => {
    setItems((prev) => {
      const existing = prev.find((p) => p.lookup_key === item.lookup_key);
      if (existing) {
        // Merge — bump quantity and, if the new add has notes, append them
        // to the existing line so nothing entered from the dish modal is lost.
        return prev.map((p) =>
          p.lookup_key === item.lookup_key
            ? {
                ...p,
                quantity: p.quantity + qty,
                notes:
                  item.notes && item.notes !== p.notes
                    ? [p.notes, item.notes].filter(Boolean).join(" · ")
                    : p.notes,
              }
            : p
        );
      }
      return [...prev, { ...item, quantity: qty }];
    });
    setOpen(true);
  }, []);

  const remove = useCallback((lookup_key) => {
    setItems((prev) => prev.filter((p) => p.lookup_key !== lookup_key));
  }, []);

  const setQty = useCallback((lookup_key, qty) => {
    if (qty <= 0) {
      setItems((prev) => prev.filter((p) => p.lookup_key !== lookup_key));
      return;
    }
    setItems((prev) =>
      prev.map((p) => (p.lookup_key === lookup_key ? { ...p, quantity: qty } : p))
    );
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const total = useMemo(
    () => items.reduce((sum, i) => sum + i.price * i.quantity, 0),
    [items]
  );
  const count = useMemo(
    () => items.reduce((sum, i) => sum + i.quantity, 0),
    [items]
  );

  const value = useMemo(
    () => ({ items, add, remove, setQty, clear, total, count, open, setOpen }),
    [items, add, remove, setQty, clear, total, count, open]
  );

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
